/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation } from "@apollo/client";
import { bindTrigger, bindMenu } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { Modal, Button } from "components/haven-design";
import { PropertyDuplicates_interestList_property_duplicates_candidates as Candidate, NotDuplicate, NotDuplicateVariables, RemoveApplicants, RemoveApplicantsVariables } from "types/gql-types";
import { trackEvent } from "services/analytics";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { REMOVE_APPLICANTS, NOT_DUPLICATE } from "./queries";
import { CandidateRow, ModalCandidateRow } from "./table-rows";

const duplicateReason = "Duplicate application";

type Props = {
  showSocial: boolean;
  candidates: Candidate[];
  matchedFields: string[];
  interestListId: string;
  slideoutEnabled: boolean;
}

const formatFieldName = (fieldName: string) => {
  switch (fieldName) {
    case "first_name":
      return "First Name";
    case "last_name":
      return "Last Name";
    case "email":
      return "Email";
    case "ssn":
      return "SSN";
    default:
      return fieldName;
  }
};

export const DuplicateTable: React.FC<Props> = (props) => {
  const [changeStatus, statusMutation] =
    useMutation<RemoveApplicants, RemoveApplicantsVariables>(REMOVE_APPLICANTS, {
      refetchQueries: ["PropertyDuplicates", "PropertyDuplicatesCount"],
    });
  const [setNotDuplicates] = useMutation<NotDuplicate, NotDuplicateVariables>(NOT_DUPLICATE, {
    refetchQueries: ["PropertyDuplicates", "PropertyDuplicatesCount"],
  });

  const [candidates, setCandidates] = React.useState([...props.candidates]);
  const candidateIds = candidates.map((c) => c.id);

  const [checkedIds, setCheckedIds] = React.useState<string[]>([]);
  const implicitSelectAll = checkedIds.length === 0;
  const checkedCount = implicitSelectAll ? candidateIds.length : checkedIds.length;

  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmRemovalModal, setConfirmRemovalModal] = React.useState(false);

  const [selectedMergeId, setSelectedMergeId] = React.useState<string | null>(null);
  const { openSnackbar } = useSnackbar();

  const popupState = usePopupState({ variant: "popover", popupId: "duplicates-actions-menu" });

  if (candidates.length === 0) {
    return null;
  }

  const getSelectedIds: () => string[] = () => (implicitSelectAll ? candidateIds : checkedIds);

  const handleMerge = (interestIdToKeep: string) => {
    const removalIds = getSelectedIds().filter(
      (id) => id !== interestIdToKeep,
    );

    changeStatus({
      variables: {
        interestListIds: [props.interestListId],
        interestIds: removalIds,
        status: "Removed",
        reason: duplicateReason,
      },
    })
      .then(() => {
        setCandidates(candidates.filter((c) => !removalIds.includes(c.id)));
        setCheckedIds([]);
        setModalOpen(false);
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Error removing duplicates. Please contact support@havenconnect.com.", status: "error" });
      });
  };

  const determineDefaultMergeId = () => {
    const selected = getSelectedIds();

    return selected.length === 0 ? null : candidates
      .filter((c) => selected.includes(c.id))
      .sort((a, b) => a.submittedAt - b.submittedAt)[0].id;
  };

  return (
    <>
      <h5>Duplicates by {props.matchedFields.map(formatFieldName).join(", ")}</h5>
      <div style={{ float: "right" }}>
        <Button
          id="duplicates-actions"
          size="S"
          color="primary"
          {...bindTrigger(popupState)}
        >
          Actions&nbsp;<span className="caret" style={{ marginLeft: 4 }} />
        </Button>
        <Menu {...bindMenu(popupState)}>
          <MenuItem id="actions-merge" onClick={() => {
            popupState.close();
            if (checkedIds.length === 1) {
              openSnackbar({ message: "Must select more than one application to merge", status: "error" });
              return;
            }

            setSelectedMergeId(determineDefaultMergeId());
            setModalOpen(true);
          }}>
                Merge ({checkedCount})
          </MenuItem>
          <MenuItem id="actions-remove" onClick={() => {
            popupState.close();
            setConfirmRemovalModal(true);
          }}>
                Remove from waitlist ({checkedCount})
          </MenuItem>
          <MenuItem id="actions-not-duplicate" onClick={() => {
            popupState.close();
            const idsToIgnore = implicitSelectAll ? candidateIds : checkedIds;

            setNotDuplicates({
              variables: {
                interestIds: idsToIgnore,
                otherCandidateIds: candidateIds,
                criteria: props.matchedFields,
              },
            })
              .then(() => {
                setCandidates(candidates.filter((c) => !idsToIgnore.includes(c.id)));
                setCheckedIds([]);
                trackEvent("Marked Not Duplicate");
              })
              .catch((err) => {
                logger.sendError(err);
                openSnackbar({ message: "Error setting not duplicate. Please contact support@havenconnect.com.", status: "error" });
              });
          }}>
                Not duplicate{checkedCount > 1 ? "s" : ""} ({checkedCount})
          </MenuItem>
        </Menu>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell component="th" style={{ width: "1%" }}>
              <label style={{ padding: 3, marginTop: 4, display: "block" }}>
                <input
                  type="checkbox"
                  checked={candidates.length === checkedIds.length}
                  onChange={(e) => {
                    const ids = e.target.checked ? candidates.map((c) => c.id) : [];
                    setCheckedIds(ids);
                  }}
                />
              </label>
            </TableCell>
            <TableCell component="th">Name</TableCell>
            <TableCell component="th">Property</TableCell>
            {props.showSocial && <TableCell component="th">SSN</TableCell>}
            <TableCell component="th">User Contact</TableCell>
            <TableCell component="th">Application Contact</TableCell>
            <TableCell component="th">Address</TableCell>
            <TableCell component="th">Submit Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates
            .sort((a, b) => a.submittedAt - b.submittedAt)
            .map((c: Candidate) => (
              <CandidateRow
                key={c.id}
                candidate={c}
                slideoutEnabled={props.slideoutEnabled}
                interestListId={props.interestListId}
                showSocial={props.showSocial}
                matchedFields={props.matchedFields}
                checkedIds={checkedIds}
                setCheckedIds={setCheckedIds}
              />
            ))}
        </TableBody>
        <Modal
          isOpen={confirmRemovalModal}
          title="Confirm Removals"
          onRequestClose={() => setConfirmRemovalModal(false)}
        >
          <DialogContent>
            {implicitSelectAll ? "This will remove all applicants in this group, are you sure?" : "Are you sure you want to remove these applicants?"}
          </DialogContent>

          <DialogActions>
            <Button
              size="L"
              color="secondary"
              marginRight
              onClick={() => setConfirmRemovalModal(false)}
            >
                Cancel
            </Button>
            <Button
              size="L"
              color="primary"
              onClick={() => {
                setConfirmRemovalModal(false);
                const interestIds = implicitSelectAll ? candidateIds : checkedIds;

                changeStatus({
                  variables: {
                    interestListIds: [props.interestListId],
                    interestIds,
                    status: "Removed",
                    reason: duplicateReason,
                  },
                })
                  .then(() => {
                    setCandidates(candidates.filter((c) => !interestIds.includes(c.id)));
                    setCheckedIds([]);
                    trackEvent("Removed Duplicate");
                  })
                  .catch((err) => {
                    logger.sendError(err);
                    openSnackbar({ message: "Error removing duplicates. Please contact support@havenconnect.com.", status: "error" });
                  });
              }}
            >
                Remove
            </Button>
          </DialogActions>
        </Modal>
      </Table>

      <Modal
        title={`Merge ${checkedCount} Applicants`}
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
      >
        <DialogContent>
          <div style={{ overflow: "hidden", overflowX: "scroll" }}>
            <p style={{ marginBottom: 25 }}>
            Select the primary application (the rest will be removed from the waitlist)
            </p>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell component="th" style={{ width: "1%" }} />
                  <TableCell component="th">Name</TableCell>
                  <TableCell component="th">Property</TableCell>
                  <TableCell component="th">Submit Date</TableCell>
                  {props.showSocial && <TableCell component="th">SSN</TableCell>}
                  <TableCell component="th">User Contact</TableCell>
                  <TableCell component="th">Application Contact</TableCell>
                  <TableCell component="th">Address</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(implicitSelectAll ? (
                  candidates
                ) : (
                  candidates.filter((c) => checkedIds.includes(c.id))
                ))
                  .sort((a, b) => a.submittedAt - b.submittedAt)
                  .map((c: Candidate, i: number) => (
                    <ModalCandidateRow
                      candidate={c}
                      earliest={i === 0}
                      key={c.id}
                      showSocial={props.showSocial}
                      matchedFields={props.matchedFields}
                      selectedMergeId={selectedMergeId}
                      setSelectedMergeId={setSelectedMergeId}
                    />
                  ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            size="L"
            marginRight
            onClick={() => {
              setModalOpen(false);
              setSelectedMergeId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={statusMutation.loading || !selectedMergeId}
            size="L"
            onClick={() => {
              if (selectedMergeId) {
                trackEvent("Merge Duplicate");
                handleMerge(selectedMergeId);
              } else {
                openSnackbar({ message: "Error selecting candidate", status: "error" });
              }
            }}
          >
            Merge ({checkedCount})
          </Button>
        </DialogActions>
      </Modal>
    </>
  );
};
